import React, { FormEventHandler } from 'react';
import { useAuthenticator } from '../AuthenticationContext';
import { useNavigate } from 'react-router-dom';

import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';

import { Notifier } from '../../system/services/notificationManager';

import { IAwaitParams } from '../types/resetPassword';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Log } from '../../system/services/logger';
import { Fade } from '@mui/material';

/**
 * This component represents the first stage in the 'reset password' workflow.
 * In this component, the user provides their user identifier (email address)
 * which is then sent off to the server for processing. The user is then
 * redirected to the 'AwaitResetToken' component as the next step.
 * @param onAwaitParams
 * @param initialEmail
 * @constructor
 */
export const IdentifyUser = ({
  onAwaitParams,
  email,
}: {
  email: string;
  onAwaitParams: (awaitParams: IAwaitParams) => void;
}) => {
  const auth = useAuthenticator();
  const navigate = useNavigate();

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (email) {
      auth
        .initiatePasswordReset(email)
        .then((instructions) => {
          Notifier.success('[Password Reset] Request sent!');
          onAwaitParams({ email, instructions });
        })
        .catch((resetError: Error) => {
          Notifier.error(
            `[Password Reset] Unable to reset password: ${resetError.message}`,
          );
        });
    }
  };

  return (
    <Fade
      style={{ transitionDuration: '1000ms' }}
      in={true}
      mountOnEnter
      unmountOnExit
    >
      <Box>
        <Grid
          container
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            width: 'auto',
            marginBottom: '10px',
          }}
        >
          <Grid xs={12} sx={{ textAlign: 'center', m: 2 }}>
            Click the submit button to have the system send a reset password
            link to {email}.
          </Grid>
          <Grid sm={2} xs={1} />
          <Grid sm={8} xs={10} sx={{ m: 1 }}>
            <Button type="submit" variant="contained" style={{ width: '100%' }}>
              Submit Reset Request
            </Button>
          </Grid>
          <Grid sm={2} xs={1} />
        </Grid>

        <Grid container width="100%">
          <Grid item xs>
            &nbsp;
          </Grid>

          <Grid item>
            <Link
              sx={{ cursor: 'pointer', marginRight: '10px' }}
              component={Link}
              onClick={() => {
                navigate('/login');
              }}
              variant="body2"
            >
              Return to Login
            </Link>
          </Grid>

          <Grid item>
            <Link
              sx={{ cursor: 'pointer' }}
              component={Link}
              onClick={() => {
                Log.warn('Clicked help!');
              }}
              variant="body2"
            >
              Need Help?
            </Link>
          </Grid>
          <Grid item xs>
            &nbsp;
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};
